import type { Metadatos } from './consultas'

export type Tono = 'positivo' | 'neutro' | 'negativo'

export interface Animo {
  tono: Tono
  /** Lo que se escribe en pantalla. El color solo acompana, nunca sustituye. */
  texto: string
}

/**
 * El asistente no guarda el estado de animo de una sola forma. En el canal de
 * clientes es una palabra (`Neutro`); en leads y en Villanueva es una nota del 1
 * al 10 escrita como cadena (`"7"`). Las dos conviven en el mismo campo.
 *
 * El nombre del campo tambien varia entre versiones del prompt de n8n, asi que
 * se buscan los alias conocidos en lugar de uno solo.
 */
const CLAVES = ['estado_emocional', 'estado_animo', 'animo', 'sentimiento'] as const

const PALABRAS: Record<string, Tono> = {
  positivo: 'positivo',
  contento: 'positivo',
  contenta: 'positivo',
  satisfecho: 'positivo',
  satisfecha: 'positivo',
  feliz: 'positivo',
  agradecido: 'positivo',
  agradecida: 'positivo',
  interesado: 'positivo',
  interesada: 'positivo',
  entusiasmado: 'positivo',
  tranquilo: 'positivo',
  neutro: 'neutro',
  neutra: 'neutro',
  neutral: 'neutro',
  indiferente: 'neutro',
  normal: 'neutro',
  negativo: 'negativo',
  enfadado: 'negativo',
  enfadada: 'negativo',
  molesto: 'negativo',
  molesta: 'negativo',
  frustrado: 'negativo',
  frustrada: 'negativo',
  insatisfecho: 'negativo',
  preocupado: 'negativo',
  preocupada: 'negativo',
  triste: 'negativo',
  enojado: 'negativo',
}

const NOTA_MAXIMA = 10

function tonoDeNota(nota: number): Tono {
  if (nota >= 7) return 'positivo'
  if (nota <= 4) return 'negativo'
  return 'neutro'
}

function capitalizar(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

function valorDeAnimo(meta: Metadatos | null | undefined): string | null {
  if (!meta || typeof meta !== 'object') return null
  for (const clave of CLAVES) {
    const valor = meta[clave]
    if (typeof valor === 'number' && Number.isFinite(valor)) return String(valor)
    if (typeof valor === 'string' && valor.trim() !== '') return valor.trim()
  }
  return null
}

/**
 * El animo de una conversacion, listo para pintar, o `null` si no se sabe.
 *
 * Una palabra que no esta en la tabla se muestra igualmente, con tono neutro:
 * el vocabulario lo escribe un prompt que puede cambiar sin avisar, y ensenar
 * el dato tal cual es preferible a etiquetarlo mal o a esconderlo.
 */
export function interpretarAnimo(meta: Metadatos | null | undefined): Animo | null {
  const valor = valorDeAnimo(meta)
  if (valor === null) return null

  const nota = Number(valor)
  if (Number.isInteger(nota) && nota >= 1 && nota <= NOTA_MAXIMA) {
    const tono = tonoDeNota(nota)
    return { tono, texto: `${capitalizar(tono)} (${nota} de ${NOTA_MAXIMA})` }
  }

  return { tono: PALABRAS[valor.toLowerCase()] ?? 'neutro', texto: capitalizar(valor) }
}
